import { getVendorWording } from '@/utils/vendorWording';
import { isPermissionValidForUserType as _isPermissionValidForUserType, type UserType as _UserType } from '@/utils/permissions';

// Re-export depuis le partage
export const isPermissionValidForUserType = _isPermissionValidForUserType;
export type UserType = _UserType;

export interface Permission {
  code: string;
  category: string;
  label: string;
  description?: string;
  userType: UserType;
}

/**
 * Genere la liste des permissions avec le wording adapte au vendorType (garantie/prestation)
 */
function buildAvailablePermissions(vendorType?: number): Permission[] {
  const wording = getVendorWording(vendorType);

  return [
  // Tableau de bord
  { code: 'dashboard.view_all', category: 'dashboard', label: 'Voir tous les utilisateurs', description: 'Filtrer par utilisateur dans le dashboard', userType: 'BOTH' },
  { code: 'dashboard.view_contracts', category: 'dashboard', label: 'Activité commerciale contrats', description: 'Voir les KPIs et le tableau des contrats', userType: 'BOTH' },
  { code: 'dashboard.view_revenue', category: 'dashboard', label: 'Activité commerciale CA', description: 'Voir les KPIs et graphiques de chiffre d\'affaires', userType: 'BOTH' },
  { code: 'dashboard.view_sav_claims', category: 'dashboard', label: 'Activité SAV pannes', description: 'Voir les KPIs et graphiques de pannes', userType: 'BOTH' },
  { code: 'dashboard.view_sav_amounts', category: 'dashboard', label: 'Activité SAV montants', description: 'Voir les montants des pannes prises en charge', userType: 'BOTH' },
  { code: 'dashboard.view_provision', category: 'dashboard', label: 'Suivi de la provision', description: 'Accéder à l\'onglet Suivi de la provision', userType: 'BOTH' },
  { code: 'dashboard.view_encaissements', category: 'dashboard', label: 'Suivi des encaissements', description: 'Accéder à l\'onglet Suivi des encaissements', userType: 'BOTH' },
  { code: 'dashboard.view_synthese', category: 'dashboard', label: 'Synthèse financière', description: 'Accéder à l\'onglet Synthèse financière', userType: 'BOTH' },
  { code: 'dashboard.view', category: 'dashboard', label: 'Tout le tableau de bord (ancien droit)', description: 'Ancien droit global : ouvre tous les onglets du tableau de bord, quels que soient les droits ci-dessus', userType: 'INTERNAL' },
  { code: 'dashboard.*', category: 'dashboard', label: 'Toutes les permissions dashboard', description: 'Accès complet au dashboard', userType: 'INTERNAL' },

  // Admin Dashboard
  { code: 'admin_dashboard.view', category: 'admin_dashboard', label: 'Tableau de bord admin', description: 'Accéder au tableau de bord global d\'administration', userType: 'INTERNAL' },

  // Societes
  { code: 'society.list', category: 'society', label: 'Liste des sociétés', description: 'Accéder à la liste des sociétés', userType: 'BOTH' },
  { code: 'society.create', category: 'society', label: 'Créer une société', description: 'Créer une nouvelle société', userType: 'BOTH' },
  { code: 'society.view_dashboard', category: 'society', label: 'Voir le tableau de bord', description: 'Accéder au tableau de bord d\'une société', userType: 'INTERNAL' },
  { code: 'society.view_informations', category: 'society', label: 'Voir les informations', description: 'Consulter les informations d\'une société', userType: 'BOTH' },
  { code: 'society.view_conditions', category: 'society', label: 'Voir les conditions', description: 'Consulter les conditions générales d\'une société', userType: 'INTERNAL' },
  { code: 'society.view_logs', category: 'society', label: 'Voir les logs', description: 'Consulter les logs de création', userType: 'INTERNAL' },
  { code: 'society.toggle_status', category: 'society', label: 'Activer/Désactiver', description: 'Activer ou désactiver une société', userType: 'BOTH' },
  { code: 'society.edit_infos', category: 'society', label: 'Modifier les informations générales', description: 'Modifier nom, contact, adresse', userType: 'INTERNAL' },
  { code: 'society.edit_sav', category: 'society', label: 'Modifier les informations SAV', description: 'Modifier email et téléphone SAV', userType: 'INTERNAL' },
  { code: 'society.edit_documents', category: 'society', label: 'Modifier les documents', description: 'Modifier les documents d\'une société', userType: 'INTERNAL' },
  { code: 'society.edit_config', category: 'society', label: 'Modifier la configuration', description: 'Modifier propriétaire et groupe parent', userType: 'INTERNAL' },
  { code: 'society.edit_options', category: 'society', label: `Modifier les options ${wording.deType}`, description: 'Modifier provision, durées, commission, etc.', userType: 'INTERNAL' },
  { code: 'society.edit_bank', category: 'society', label: 'Modifier les informations bancaires', description: 'Modifier IBAN, débiteur, banque', userType: 'INTERNAL' },
  { code: 'society.manage_presets', category: 'society', label: 'Gérer les presets', description: 'Créer et modifier les presets de société', userType: 'INTERNAL' },
  { code: 'society.export', category: 'society', label: 'Exporter les vendeurs', description: 'Exporter la liste des vendeurs en XLSX', userType: 'INTERNAL' },
  { code: 'society.rib_analysis', category: 'society', label: 'Analyser un RIB', description: 'Extraire automatiquement les coordonnées bancaires d\'un RIB', userType: 'INTERNAL' },
  { code: 'society.*', category: 'society', label: 'Toutes les permissions sociétés', description: 'Accès complet aux sociétés', userType: 'INTERNAL' },

  // Reparateurs
  { code: 'repairer.list', category: 'repairer', label: 'Liste des réparateurs', description: 'Accéder à la liste des réparateurs', userType: 'INTERNAL' },
  { code: 'repairer.*', category: 'repairer', label: 'Toutes les permissions réparateurs', description: 'Accès complet aux réparateurs', userType: 'INTERNAL' },

  // Utilisateurs internes
  { code: 'internal_user.list', category: 'internal_user', label: 'Liste des utilisateurs internes', description: 'Accéder à la liste des utilisateurs internes', userType: 'INTERNAL' },
  { code: 'internal_user.create', category: 'internal_user', label: 'Créer un utilisateur interne', description: 'Créer un nouvel utilisateur interne', userType: 'INTERNAL' },
  { code: 'internal_user.edit', category: 'internal_user', label: 'Modifier un utilisateur interne', description: "Modifier un utilisateur interne", userType: 'INTERNAL' },
  { code: 'internal_user.edit_permissions', category: 'internal_user', label: 'Modifier les permissions', description: "Modifier les permissions d'un utilisateur interne", userType: 'INTERNAL' },
  { code: 'internal_user.delete', category: 'internal_user', label: 'Supprimer un utilisateur interne', description: 'Supprimer un utilisateur interne', userType: 'INTERNAL' },
  { code: 'internal_user.*', category: 'internal_user', label: 'Toutes les permissions utilisateurs internes', description: 'Accès complet aux utilisateurs internes', userType: 'INTERNAL' },

  // Utilisateurs externes
  { code: 'external_user.list', category: 'external_user', label: 'Liste des utilisateurs', description: 'Accéder à la liste des utilisateurs externes', userType: 'BOTH' },
  { code: 'external_user.view', category: 'external_user', label: 'Voir les détails', description: "Voir les détails d'un utilisateur externe", userType: 'BOTH' },
  { code: 'external_user.create', category: 'external_user', label: 'Créer un utilisateur', description: 'Créer un nouvel utilisateur externe', userType: 'BOTH' },
  { code: 'external_user.edit_personal_info', category: 'external_user', label: 'Modifier les infos personnelles', description: "Modifier les informations personnelles d'un utilisateur", userType: 'BOTH' },
  { code: 'external_user.edit_societies', category: 'external_user', label: 'Gérer les sociétés', description: "Assigner/retirer des sociétés d'un utilisateur", userType: 'BOTH' },
  { code: 'external_user.edit_password', category: 'external_user', label: 'Modifier le mot de passe', description: "Définir/modifier le mot de passe d'un utilisateur", userType: 'BOTH' },
  { code: 'external_user.edit_permissions', category: 'external_user', label: 'Gérer les permissions', description: "Modifier les permissions d'un utilisateur", userType: 'BOTH' },
  { code: 'external_user.promote', category: 'external_user', label: 'Changer l\'administrateur d\'une société', description: 'Promouvoir un utilisateur administrateur de sa société (l\'administrateur actuel devient utilisateur)', userType: 'INTERNAL' },
  { code: 'external_user.impersonate', category: 'external_user', label: 'Se connecter en tant que', description: "Se connecter en tant qu'un utilisateur externe", userType: 'INTERNAL' },
  { code: 'external_user.view_audit_logs', category: 'external_user', label: 'Voir l\'historique des modifications', description: "Consulter l'historique des modifications d'un utilisateur", userType: 'INTERNAL' },
  { code: 'external_user.resend_welcome_email', category: 'external_user', label: 'Renvoyer l\'email de bienvenue', description: 'Renvoyer un email de bienvenue avec un nouveau mot de passe temporaire', userType: 'BOTH' },
  { code: 'external_user.delete', category: 'external_user', label: 'Supprimer un utilisateur', description: 'Supprimer un utilisateur externe', userType: 'BOTH' },
  { code: 'external_user.export', category: 'external_user', label: 'Exporter les utilisateurs', description: 'Exporter la liste des utilisateurs (nom, prénom, email, société) en XLSX', userType: 'INTERNAL' },
  { code: 'external_user.*', category: 'external_user', label: 'Toutes les permissions utilisateurs', description: 'Accès complet aux utilisateurs externes', userType: 'INTERNAL' },

  // Garanties / Prestations
  { code: 'warranty.list', category: 'warranty', label: `Liste des ${wording.pluralLower}`, description: `Accéder à la liste des ${wording.pluralLower}`, userType: 'BOTH' },
  { code: 'warranty.view', category: 'warranty', label: `Voir les ${wording.pluralLower}`, description: `Consulter les ${wording.pluralLower}`, userType: 'BOTH' },
  { code: 'warranty.create', category: 'warranty', label: `Créer ${wording.withIndefinite}`, description: `Créer une nouvelle ${wording.singularLower}`, userType: 'BOTH' },
  { code: 'warranty.edit', category: 'warranty', label: `Modifier ${wording.withIndefinite}`, description: `Modifier les informations d'${wording.withIndefinite}`, userType: 'BOTH' },
  { code: 'warranty.edit_price', category: 'warranty', label: 'Modifier le prix de vente', description: `Modifier le prix de vente d'${wording.withIndefinite} ou d'un contrat`, userType: 'BOTH' },
  { code: 'warranty.edit_color', category: 'warranty', label: 'Modifier la couleur', description: `Modifier la couleur d'${wording.withIndefinite}`, userType: 'BOTH' },
  { code: 'warranty.reorder', category: 'warranty', label: `Réorganiser les ${wording.pluralLower}`, description: `Changer l'ordre d'affichage des ${wording.pluralLower}`, userType: 'BOTH' },
  { code: 'warranty.duplicate', category: 'warranty', label: `Dupliquer ${wording.withIndefinite}`, description: `Dupliquer ${wording.withIndefinite} existante`, userType: 'BOTH' },
  { code: 'warranty.toggle_status', category: 'warranty', label: `Activer/Désactiver ${wording.withIndefinite}`, description: `Activer ou désactiver ${wording.withIndefinite}`, userType: 'BOTH' },
  { code: 'warranty.update_provisions', category: 'warranty', label: 'Mettre à jour les provisions', description: 'Mettre à jour les provisions de tous les contrats actifs', userType: 'BOTH' },
  { code: 'warranty.download_cgp', category: 'warranty', label: 'Télécharger les CGP', description: 'Télécharger les conditions générales particulières', userType: 'BOTH' },
  { code: 'warranty.delete', category: 'warranty', label: 'Supprimer un preset', description: 'Supprimer un preset de garantie ou de couverture', userType: 'INTERNAL' },
  { code: 'warranty.view_diagnostic', category: 'warranty', label: 'Voir le diagnostic', description: `Consulter et modifier le champ diagnostic des ${wording.pluralLower}`, userType: 'INTERNAL' },
  { code: 'warranty.manage_recommendations', category: 'warranty', label: 'Gérer les recommandations', description: `Accéder à la gestion des recommandations de ${wording.pluralLower}`, userType: 'INTERNAL' },
  { code: 'warranty.*', category: 'warranty', label: `Toutes les permissions ${wording.pluralLower}`, description: `Accès complet aux ${wording.pluralLower}`, userType: 'INTERNAL' },

  // Contrats
  { code: 'contract.list', category: 'contract', label: 'Liste des contrats', description: 'Accéder à la liste des contrats', userType: 'BOTH' },
  { code: 'contract.create', category: 'contract', label: 'Créer un contrat', description: 'Créer un nouveau contrat client', userType: 'BOTH' },
  { code: 'contract.view_all', category: 'contract', label: 'Voir tous les contrats', description: 'Voir les contrats de tous les utilisateurs de la société', userType: 'BOTH' },
  { code: 'contract.request_cancellation', category: 'contract', label: 'Demander l\'annulation', description: 'Demander l\'annulation d\'un contrat', userType: 'BOTH' },
  { code: 'contract.cancel', category: 'contract', label: 'Annuler un contrat', description: 'Annuler un contrat (annule signature YouSign et paiement Stripe)', userType: 'INTERNAL' },
  { code: 'contract.offer', category: 'contract', label: 'Offrir un contrat', description: 'Offrir un contrat (crée un avoir pour le prescripteur)', userType: 'INTERNAL' },
  { code: 'contract.use_test_data', category: 'contract', label: 'Utiliser les données de test', description: 'Remplir automatiquement les formulaires avec des données de test', userType: 'INTERNAL' },
  { code: 'contract.view_audit_logs', category: 'contract', label: 'Voir l\'historique des modifications', description: 'Consulter l\'historique des modifications d\'un contrat', userType: 'INTERNAL' },
  { code: 'contract.recalc', category: 'contract', label: 'Recalcul rétroactif des contrats', description: 'Prévisualiser et appliquer le recalcul rétroactif des contrats', userType: 'INTERNAL' },
  { code: 'contract.recreate_subscription', category: 'contract', label: 'Recréer un abonnement Stripe', description: 'Recréer l\'abonnement Stripe d\'un contrat mensualisé', userType: 'INTERNAL' },
  { code: 'contract.*', category: 'contract', label: 'Toutes les permissions contrats', description: 'Accès complet aux contrats', userType: 'INTERNAL' },

  // Attestations de livraison
  { code: 'delivery_proof.list', category: 'delivery_proof', label: 'Liste des attestations', description: 'Accéder à la liste des attestations de livraison', userType: 'BOTH' },
  { code: 'delivery_proof.create', category: 'delivery_proof', label: 'Créer une attestation', description: 'Créer une attestation', userType: 'BOTH' },
  { code: 'delivery_proof.view_all', category: 'delivery_proof', label: 'Voir toutes les attestations', description: 'Voir les attestations créées par tous les utilisateurs de la société', userType: 'BOTH' },
  { code: 'delivery_proof.*', category: 'delivery_proof', label: 'Toutes les permissions attestations', description: 'Accès complet aux attestations', userType: 'INTERNAL' },

  // SAV
  { code: 'sav.list', category: 'sav', label: 'Liste des SAV', description: 'Accéder à la liste des SAV', userType: 'BOTH' },
  { code: 'sav.view', category: 'sav', label: 'Voir les dossiers SAV', description: 'Consulter le détail d\'un dossier SAV', userType: 'BOTH' },
  { code: 'sav.view_all', category: 'sav', label: 'Voir tous les SAV', description: 'Voir les SAV de toutes les sociétés', userType: 'BOTH' },
  { code: 'sav.manage', category: 'sav', label: 'Gérer les SAV', description: 'Gérer et traiter les dossiers SAV', userType: 'BOTH' },
  { code: 'sav.sign', category: 'sav', label: 'Signer les bons de décision', description: 'Signer les bons de décision SAV via YouSign', userType: 'BOTH' },
  { code: 'sav.export', category: 'sav', label: 'Exporter les SAV', description: 'Télécharger l\'export CSV des SAV depuis le dashboard admin', userType: 'INTERNAL' },
  { code: 'sav.backoffice', category: 'sav', label: 'Accéder à la gestion SAV', description: 'Traiter les dossiers dans l\'outil Gestion SAV', userType: 'INTERNAL' },
  { code: 'sav.*', category: 'sav', label: 'Toutes les permissions SAV', description: 'Accès complet aux SAV', userType: 'INTERNAL' },

  // Administration du SAV. Catégorie dédiée et non `sav.*`, que portent les COMMERCIAL.
  { code: 'sav_admin.audit', category: 'sav_admin', label: 'Audit et cohérence SAV', description: 'Contrôler la cohérence des montants SAV et des garants, recalculer les montants', userType: 'INTERNAL' },
  { code: 'sav_admin.manage_tags', category: 'sav_admin', label: 'Gérer les tags SAV', description: 'Modifier et supprimer les tags', userType: 'INTERNAL' },
  { code: 'sav_admin.signature_tools', category: 'sav_admin', label: 'Outils de signature SAV', description: 'Resynchroniser l\'état des signatures YouSign et consulter leur diagnostic', userType: 'INTERNAL' },
  { code: 'sav_admin.*', category: 'sav_admin', label: 'Toutes les permissions administration SAV', description: 'Accès complet aux outils d\'administration du SAV', userType: 'INTERNAL' },

  // Factures
  { code: 'invoice.list', category: 'invoice', label: 'Liste des factures', description: 'Accéder à la liste des factures', userType: 'BOTH' },
  { code: 'invoice.view_child_companies', category: 'invoice', label: 'Voir les factures des agences', description: 'Voir aussi les factures des sociétés filles (agences) du groupe', userType: 'BOTH' },
  { code: 'invoice.admin', category: 'invoice', label: 'Administration des factures', description: 'Accéder à la gestion admin des factures', userType: 'INTERNAL' },
  { code: 'invoice.change_state', category: 'invoice', label: 'Changer l\'état d\'une facture', description: 'Modifier manuellement l\'état d\'une facture', userType: 'INTERNAL' },
  { code: 'invoice.regenerate_pdf', category: 'invoice', label: 'Régénérer le PDF d\'une facture', description: 'Prévisualiser puis régénérer le PDF d\'une facture', userType: 'INTERNAL' },
  { code: 'invoice.rerun', category: 'invoice', label: 'Relancer la facturation', description: 'Relancer la facturation d\'une facture', userType: 'INTERNAL' },
  { code: 'invoice.credit_note', category: 'invoice', label: 'Émettre un avoir', description: 'Annuler une facture par un avoir et libérer ses lignes', userType: 'INTERNAL' },
  { code: 'invoice.send_email', category: 'invoice', label: 'Envoyer une facture par email', description: 'Envoyer le PDF d\'une facture ou d\'un avoir au vendeur par email', userType: 'INTERNAL' },
  { code: 'invoice.send_bank', category: 'invoice', label: 'Envoyer à la banque', description: 'Envoyer les fichiers de prélèvement à la banque', userType: 'INTERNAL' },
  { code: 'invoice.download_billing', category: 'invoice', label: 'Télécharger le fichier de facturation', description: 'Télécharger le fichier de facturation mensuel', userType: 'INTERNAL' },
  { code: 'invoice.download_sepa', category: 'invoice', label: 'Télécharger le fichier des prélèvements', description: 'Télécharger le fichier SEPA des prélèvements mensuels', userType: 'INTERNAL' },
  { code: 'invoice.download_netcash', category: 'invoice', label: 'Télécharger le fichier Netcash', description: 'Télécharger le fichier Netcash mensuel', userType: 'INTERNAL' },
  { code: 'invoice.*', category: 'invoice', label: 'Toutes les permissions factures', description: 'Accès complet aux factures', userType: 'INTERNAL' },

  // Tarifications catalogue
  { code: 'catalog_pricing.list', category: 'catalog_pricing', label: 'Accéder aux tarifications', description: 'Consulter et gérer les prix catalogue', userType: 'INTERNAL' },
  { code: 'catalog_pricing.*', category: 'catalog_pricing', label: 'Toutes les permissions tarifications', description: 'Accès complet aux tarifications', userType: 'INTERNAL' },

  // Frais de service
  { code: 'invoice_fees.list', category: 'invoice_fees', label: 'Accéder aux frais de service', description: 'Consulter les frais de service prélevés', userType: 'INTERNAL' },
  { code: 'invoice_fees.*', category: 'invoice_fees', label: 'Toutes les permissions frais de service', description: 'Accès complet aux frais de service', userType: 'INTERNAL' },

  // PLV
  { code: 'plv.list', category: 'plv', label: 'Voir le catalogue PLV', description: 'Accéder au catalogue PLV', userType: 'BOTH' },
  { code: 'plv.orders', category: 'plv', label: 'Voir les commandes PLV', description: 'Accéder à l\'onglet Commandes PLV', userType: 'BOTH' },
  { code: 'plv.products', category: 'plv', label: 'Voir les produits PLV', description: 'Accéder à l\'onglet Produits PLV', userType: 'BOTH' },
  { code: 'plv.templates', category: 'plv', label: 'Gérer les templates PLV', description: 'Accéder à l\'onglet Templates PLV', userType: 'INTERNAL' },
  { code: 'plv.create_template', category: 'plv', label: 'Créer un template PLV', description: 'Créer un nouveau template PLV', userType: 'INTERNAL' },
  { code: 'plv.create_product', category: 'plv', label: 'Créer un produit PLV', description: 'Créer un produit à partir d\'un template', userType: 'INTERNAL' },
  { code: 'plv.*', category: 'plv', label: 'Toutes les permissions PLV', description: 'Accès complet à la PLV', userType: 'INTERNAL' },

  // Webhooks
  { code: 'webhooks.list', category: 'webhooks', label: 'Liste des webhooks', description: 'Accéder à la liste des webhooks', userType: 'INTERNAL' },
  { code: 'webhooks.*', category: 'webhooks', label: 'Toutes les permissions webhooks', description: 'Accès complet aux webhooks', userType: 'INTERNAL' },

  // Stripe
  { code: 'stripe.account', category: 'stripe', label: 'Gérer le compte Stripe', description: 'Créer et finaliser l\'onboarding du compte Stripe', userType: 'BOTH' },
  { code: 'stripe.self', category: 'stripe', label: 'Administration Stripe (lecture)', description: 'Accéder au dashboard Stripe en lecture seule (paiements, payouts, contrats)', userType: 'BOTH' },

  // UpSell (plateforme commerciale interne)
  { code: 'upsell.access', category: 'upsell', label: 'Accéder à la plateforme UpSell', description: 'Traiter les leads upsell : appels, envoi d\'offres, pré-upsell', userType: 'INTERNAL' },
  { code: 'upsell.dashboard', category: 'upsell', label: 'Voir la performance UpSell', description: 'Page Performance de la plateforme et analytics admin (vue équipe)', userType: 'INTERNAL' },
  { code: 'upsell.admin', category: 'upsell', label: 'Administrer l\'UpSell', description: 'Config vendeurs, visibilité des garanties, settings, prolongation d\'offres', userType: 'INTERNAL' },
  { code: 'upsell.*', category: 'upsell', label: 'Toutes les permissions UpSell', description: 'Accès complet à l\'UpSell (plateforme + performance + administration)', userType: 'INTERNAL' },

  // Clés API publiques (afkey_) des sociétés clientes.
  // Catégorie dédiée et non `society.manage_api_keys` : le wildcard `society.*`
  // porté par les SUPERVISOR ouvrirait sinon la gestion des clés à tous.
  { code: 'api_key.manage', category: 'api_key', label: 'Gérer les clés API', description: 'Créer et révoquer les clés API publiques d\'une société cliente', userType: 'INTERNAL' },
  { code: 'api_key.*', category: 'api_key', label: 'Toutes les permissions clés API', description: 'Accès complet aux clés API', userType: 'INTERNAL' },

  // Nouveautés produit. Catégorie dédiée, comme `api_key` : aucun wildcard de
  // catégorie existant ne doit l'accorder par ricochet.
  { code: 'news.manage', category: 'news', label: 'Gérer les nouveautés', description: 'Rédiger, cibler, publier et dépublier les nouveautés diffusées sur les plateformes', userType: 'INTERNAL' },
  { code: 'news.*', category: 'news', label: 'Toutes les permissions nouveautés', description: 'Accès complet aux nouveautés', userType: 'INTERNAL' },

  // Argent des mensualités d'un contrat. Catégorie dédiée et non `contract.*`,
  // que portent les COMMERCIAL et les SUPERVISOR.
  { code: 'contract_payment.postpone_first_charge', category: 'contract_payment', label: 'Rembourser et prélever à la date de début', description: 'Rembourser le 1er prélèvement d\'un contrat mensualisé, rendre la commission au vendeur et décaler les prélèvements à la date de début du contrat', userType: 'INTERNAL' },
  { code: 'contract_payment.*', category: 'contract_payment', label: 'Toutes les permissions mensualités', description: 'Accès complet aux actions sur les mensualités des contrats', userType: 'INTERNAL' },

  // Bandeau affiché sur le site de déclaration de sinistre.
  { code: 'declaration_banner.manage', category: 'declaration_banner', label: 'Gérer le bandeau de déclaration', description: 'Configurer le bandeau affiché sur le site de déclaration de sinistre', userType: 'INTERNAL' },
  { code: 'declaration_banner.*', category: 'declaration_banner', label: 'Toutes les permissions bandeau', description: 'Accès complet au bandeau de déclaration', userType: 'INTERNAL' },

  // Contrôles d'intégrité des données et leurs actions de réparation.
  { code: 'integrity_check.run', category: 'integrity_check', label: 'Lancer les checks d\'intégrité', description: 'Consulter et lancer les contrôles d\'intégrité des données et leurs réparations', userType: 'INTERNAL' },
  { code: 'integrity_check.*', category: 'integrity_check', label: 'Toutes les permissions checks d\'intégrité', description: 'Accès complet aux checks d\'intégrité', userType: 'INTERNAL' },
  ];
}

/** Liste par defaut (wording Garantie) */
export const AVAILABLE_PERMISSIONS: Permission[] = buildAvailablePermissions();

/**
 * Options societe qui conditionnent la visibilite de certaines categories de permissions.
 */
export interface SocietyOptionsFilter {
  deliveryProofCreate?: number; // 0=off, 2=on
  defineProvision?: boolean; // false = provision désactivée sur la société
  isWarrantyPolicyOwner?: boolean; // false = société membre dont la stratégie garantie est portée par le parent
  // UUID brute du garant. « Garant externe » = renseigné ET différent de la
  // société elle-même. Auto-garant peut se représenter par NULL OU par sa
  // propre UUID : les deux sont équivalents (cf. ownSocietyId, isExternalSocietyRef).
  guarantorId?: string | null;
  isTransferPrice?: boolean; // true = mode rétrocession activé
  // UUID brute de la société facturée. « Facturation portée par un parent » =
  // renseigné ET différent de la société elle-même (même règle que guarantorId).
  billedId?: string | null;
  lockChildrenPriceEditing?: boolean; // true = le groupe interdit la modification du prix de vente aux enfants
  // UUID de la société configurée — sert à distinguer un garant/facturé EXTERNE
  // d'une auto-référence (guarantorId/billedId === ownSocietyId). Sans elle, une
  // valeur self est traitée comme externe (ancien bug du bouton signature SAV).
  ownSocietyId?: string | null;
}

/**
 * Vrai si `ref` désigne une société EXTERNE (garant ou facturé) : renseignée et
 * différente de la société elle-même. NULL ou auto-référence → false. Source de
 * vérité unique pour « la société délègue à un tiers » — évite que chaque
 * appelant normalise self→null de son côté.
 */
export const isExternalSocietyRef = (
  ref: string | null | undefined,
  ownSocietyId: string | null | undefined,
): boolean => !!ref && ref !== ownSocietyId;

const SOCIETY_OPTION_TO_CATEGORY: Record<'deliveryProofCreate', { category: string; activeValue: number }> = {
  deliveryProofCreate: { category: 'delivery_proof', activeValue: 2 },
};

/** Permission masquée si la provision n'est pas activée sur la société */
const PROVISION_PERMISSION = 'dashboard.view_provision';

/**
 * Permissions de gestion des garanties réservées au propriétaire de la stratégie garantie.
 * Masquées pour les sociétés membres dont warrantyPolicyOwnerId pointe vers le parent.
 */
/**
 * Permissions masquées pour les sociétés avec garant externe et transferPrice activé.
 * Ces sociétés n'ont pas accès au SAV, à la provision, aux encaissements ni à la synthèse.
 */
const GUARANTOR_TRANSFER_HIDDEN_PERMISSIONS = [
  'dashboard.view_sav_claims',
  'dashboard.view_sav_amounts',
  'dashboard.view_provision',
  'dashboard.view_encaissements',
  'dashboard.view_synthese',
];

/**
 * Permissions SAV masquées quand la société a un garant externe (guarantorId != null).
 * Seule sav.list reste visible.
 */
const GUARANTOR_HIDDEN_SAV_PERMISSIONS = [
  'sav.view',
  'sav.view_all',
  'sav.manage',
  'sav.sign',
];

/**
 * Permissions masquées quand la facturation est portée par un parent (billedId != null).
 */
const BILLED_HIDDEN_PERMISSIONS = [
  'invoice.list',
  'plv.list',
  'plv.orders',
  'plv.products',
  'stripe.account',
  'stripe.self',
];

const WARRANTY_MANAGEMENT_PERMISSIONS = [
  'warranty.create',
  'warranty.edit',
  'warranty.edit_price',
  'warranty.edit_color',
  'warranty.reorder',
  'warranty.duplicate',
  'warranty.toggle_status',
  'warranty.update_provisions',
];

/**
 * Filtre les permissions disponibles selon le type d'utilisateur, le vendorType,
 * et les options activees sur la societe.
 */
export const getPermissionsForUserType = (
  userType: 'INTERNAL' | 'EXTERNAL',
  vendorType?: number,
  societyOptions?: SocietyOptionsFilter,
): Permission[] => {
  const permissions = buildAvailablePermissions(vendorType);
  // Garant/facturation EXTERNES uniquement (auto-référence = pas externe).
  const hasExternalGuarantor = isExternalSocietyRef(societyOptions?.guarantorId, societyOptions?.ownSocietyId);
  const hasExternalBilled = isExternalSocietyRef(societyOptions?.billedId, societyOptions?.ownSocietyId);
  return permissions.filter(permission => {
    if (permission.userType !== 'BOTH' && permission.userType !== userType) return false;
    if (societyOptions) {
      for (const [optionKey, { category, activeValue }] of Object.entries(SOCIETY_OPTION_TO_CATEGORY)) {
        const optionValue = societyOptions[optionKey as keyof typeof SOCIETY_OPTION_TO_CATEGORY];
        if (permission.category === category && optionValue !== undefined && optionValue !== activeValue) {
          return false;
        }
      }
      // Masquer la permission provision si la société n'a pas la provision activée
      if (societyOptions.defineProvision === false && permission.code === PROVISION_PERMISSION) {
        return false;
      }
      // Masquer les permissions de gestion garantie si la société n'est pas propriétaire
      if (societyOptions.isWarrantyPolicyOwner === false && WARRANTY_MANAGEMENT_PERMISSIONS.includes(permission.code)) {
        return false;
      }
      // Masquer les permissions SAV/financières pour les sociétés avec garant externe + transferPrice
      if (hasExternalGuarantor && !!societyOptions.isTransferPrice && GUARANTOR_TRANSFER_HIDDEN_PERMISSIONS.includes(permission.code)) {
        return false;
      }
      // Masquer les permissions SAV (sauf sav.list) pour les sociétés avec garant externe
      if (hasExternalGuarantor && GUARANTOR_HIDDEN_SAV_PERMISSIONS.includes(permission.code)) {
        return false;
      }
      // Masquer factures/PLV/Stripe si la facturation est portée par un parent
      if (hasExternalBilled && BILLED_HIDDEN_PERMISSIONS.includes(permission.code)) {
        return false;
      }
      // Masquer la modification du prix de vente si le groupe l'a verrouillé
      if (societyOptions.lockChildrenPriceEditing && permission.code === 'warranty.edit_price') {
        return false;
      }
    }
    return true;
  });
};

/**
 * Recupere les permissions disponibles pour un utilisateur specifique
 */
export const getAvailablePermissionsForUser = (user: { type: 'INTERNAL' | 'EXTERNAL' }, vendorType?: number): Permission[] => {
  return getPermissionsForUserType(user.type, vendorType);
};

/**
 * Filtre une liste de permissions pour ne garder que celles valides pour un type d'utilisateur
 */
export const filterPermissionsForUserType = (
  permissions: string[],
  userType: 'INTERNAL' | 'EXTERNAL'
): string[] => {
  return permissions.filter(permCode => isPermissionValidForUserType(permCode, userType));
};

/**
 * Recupere les permissions avec le wording adapte au vendorType et filtrage par options societe
 */
export const getAvailablePermissionsWithWording = (vendorType?: number, societyOptions?: SocietyOptionsFilter): Permission[] => {
  const permissions = buildAvailablePermissions(vendorType);
  if (!societyOptions) return permissions;
  // Garant/facturation EXTERNES uniquement (auto-référence = pas externe).
  const hasExternalGuarantor = isExternalSocietyRef(societyOptions.guarantorId, societyOptions.ownSocietyId);
  const hasExternalBilled = isExternalSocietyRef(societyOptions.billedId, societyOptions.ownSocietyId);
  return permissions.filter(permission => {
    for (const [optionKey, { category, activeValue }] of Object.entries(SOCIETY_OPTION_TO_CATEGORY)) {
      const optionValue = societyOptions[optionKey as keyof typeof SOCIETY_OPTION_TO_CATEGORY];
      if (permission.category === category && optionValue !== undefined && optionValue !== activeValue) {
        return false;
      }
    }
    // Masquer la permission provision si la société n'a pas la provision activée
    if (societyOptions.defineProvision === false && permission.code === PROVISION_PERMISSION) {
      return false;
    }
    // Masquer les permissions de gestion garantie si la société n'est pas propriétaire
    if (societyOptions.isWarrantyPolicyOwner === false && WARRANTY_MANAGEMENT_PERMISSIONS.includes(permission.code)) {
      return false;
    }
    // Masquer les permissions SAV/financières pour les sociétés avec garant externe + transferPrice
    if (hasExternalGuarantor && !!societyOptions.isTransferPrice && GUARANTOR_TRANSFER_HIDDEN_PERMISSIONS.includes(permission.code)) {
      return false;
    }
    // Masquer les permissions SAV (sauf sav.list) pour les sociétés avec garant externe
    if (hasExternalGuarantor && GUARANTOR_HIDDEN_SAV_PERMISSIONS.includes(permission.code)) {
      return false;
    }
    // Masquer factures/PLV/Stripe si la facturation est portée par un parent
    if (hasExternalBilled && BILLED_HIDDEN_PERMISSIONS.includes(permission.code)) {
      return false;
    }
    // Masquer la modification du prix de vente si le groupe l'a verrouillé
    if (societyOptions.lockChildrenPriceEditing && permission.code === 'warranty.edit_price') {
      return false;
    }
    return true;
  });
};

export const DEFAULT_PERMISSIONS_BY_ROLE: Record<string, string[]> = {
  // Utilisateurs externes (correspondance Symfony)
  ADMIN: getPermissionsForUserType('EXTERNAL').map(p => p.code),
  ACCOUNTANT: [
    'dashboard.view_all',
    'dashboard.view_contracts',
    'dashboard.view_revenue',
    'dashboard.view_sav_claims',
    'dashboard.view_sav_amounts',
    'dashboard.view_provision',
    'dashboard.view_encaissements',
    'dashboard.view_synthese',
    'contract.list',
    'contract.view_all',
    'delivery_proof.list',
    'delivery_proof.view_all',
    'sav.list',
    'sav.view_all',
    'invoice.list',
  ],
  SALES_MANAGER: [
    'dashboard.view_all',
    'dashboard.view_contracts',
    'dashboard.view_revenue',
    'dashboard.view_sav_claims',
    'warranty.list',
    'warranty.view',
    'warranty.create',
    'warranty.edit',
    'warranty.edit_price',
    'contract.create',
    'contract.list',
    'contract.view_all',
    'contract.request_cancellation',
    'delivery_proof.create',
    'delivery_proof.list',
    'delivery_proof.view_all',
    'sav.view_all',
    'external_user.list',
    'external_user.view',
    'external_user.create',
    'plv.list',
    'plv.orders',
  ],
  SALES: [
    'dashboard.view_contracts',
    'dashboard.view_sav_claims',
    'sav.list',
    'warranty.list',
    'warranty.view',
    'warranty.edit_price',
    'contract.create',
    'contract.list',
    'contract.request_cancellation',
    'delivery_proof.create',
    'delivery_proof.list'
  ],
  SAV_MANAGER: [
    'dashboard.view_all',
    'dashboard.view_contracts',
    'dashboard.view_sav_claims',
    'dashboard.view_sav_amounts',
    'dashboard.view_provision',
    'sav.list',
    'sav.view',
    'sav.view_all',
    'sav.manage',
    'sav.sign',
    'contract.list',
    'contract.view_all',
    'delivery_proof.list',
    'delivery_proof.view_all'
  ],
  SAV_COORDINATOR: [
    'dashboard.view_all',
    'dashboard.view_contracts',
    'dashboard.view_sav_claims',
    'dashboard.view_sav_amounts',
    'dashboard.view_provision',
    'sav.list',
    'sav.view',
    'sav.view_all',
    'contract.list',
    'contract.view_all'
  ],

  // Utilisateurs internes : toutes les permissions à la création, on en retire
  // ensuite depuis la fiche. Le type de rôle ne décide plus des droits.
  SUPER_ADMIN: ['*'],
  SUPERVISOR: ['*'],
  COMMERCIAL: ['*'],
  // Profil restreint à la plateforme upsell + page Performance
  UPSELL: ['upsell.access', 'upsell.dashboard'],
};
